import { useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { settingsService } from "services/settings/settings.service"
import { DepositsAndWithdrawalsLimits } from "services/settings/type"

import { LimitsDepositFields } from "./LimitsDepositFields"
import { LimitsPerCurrencySection } from "./LimitsPerCurrencySection"
import { LimitsWithdrawalFields } from "./LimitsWithdrawalFields"
import { SettingsHistoryNote } from "./SettingsHistoryNote"
import { SettingsSectionActions } from "./SettingsSectionActions"

interface LimitsTabProps {
  limits: DepositsAndWithdrawalsLimits
  currencies: Record<string, boolean>
  onChange: (limits: DepositsAndWithdrawalsLimits) => void
  refreshSettings: () => Promise<void>
  onSaved?: () => void
}

export const LimitsTab = ({ limits, currencies, onChange, refreshSettings, onSaved }: LimitsTabProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [saving, setSaving] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)

  const currencyLimits = limits.currencyLimits ?? {}

  // Every platform currency except USD (USD is the base, applied to the rest via FX) and any already added.
  const availableCurrencies = Object.keys(currencies)
    .filter((code) => code !== "USD" && !currencyLimits[code])
    .sort()

  const addCurrency = (code: string) => {
    if (!code || currencyLimits[code]) return
    onChange({
      ...structuredClone(limits),
      currencyLimits: {
        ...currencyLimits,
        [code]: {
          deposit: { ftd: { min: 0, max: 0 }, std: { min: 0, max: 0 } },
          withdrawal: { min: 0, max: 0 }
        }
      }
    })
  }

  const removeCurrency = (code: string) => {
    const rest = { ...currencyLimits }
    delete rest[code]
    onChange({ ...structuredClone(limits), currencyLimits: rest })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      onChange(limits)
      const isSaved = await settingsService.updateWithdrawalsAndDepositsLimits(limits)
      if (isSaved) {
        toast.success(t("settings.limits.toastUpdated") as string)
        onSaved?.()
        setHistoryKey((key) => key + 1)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleResetDone = async () => {
    await refreshSettings()
    setHistoryKey((key) => key + 1)
  }

  const updateLimit = (path: string[], value: string) => {
    const newLimits = structuredClone(limits)
    let obj: any = newLimits

    for (let i = 0; i < path.length - 1; i++) {
      obj = obj[path[i]]
    }
    obj[path[path.length - 1]] = parseFloat(value) || 0

    onChange(newLimits)
  }

  return (
    <section className="flex flex-col gap-6 mt-6">
      <h3 className="text-xl font-semibold mb-2 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.limits.title")}</h3>

      <p className="text-sm text-gray-500 dark:text-gray-400 -mt-2">{t("settings.limits.description")}</p>
      <SettingsHistoryNote target="limits" refreshKey={historyKey} />
      {/* WARNING - 0 = no limit */}
      <div className="bg-warning/10 border-warning px-6 py-3 w-fit">
        <p className="text-sm text-warning">{t("settings.limits.warningZero")}</p>
      </div>

      <h4 className="text-lg font-semibold mb-2 text-[rgba(6,7,21,1)] dark:text-white mt-24">{t("settings.limits.depositLimits")}</h4>
      <h5 className="text-md font-semibold mt-2 mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.limits.fiatDeposits")}</h5>
      <LimitsDepositFields
        idPrefix="deposit-fiat"
        basePath={["deposit", "fiat"]}
        values={limits.deposit.fiat}
        typeLabel={t("settings.limits.types.fiatDeposit")}
        onUpdate={updateLimit}
        disabled={saving}
      />
      <h5 className="text-md font-semibold mt-2 mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.limits.cryptoDeposits")}</h5>
      <LimitsDepositFields
        idPrefix="deposit-crypto"
        basePath={["deposit", "crypto"]}
        values={limits.deposit.crypto}
        typeLabel={t("settings.limits.types.cryptoDeposit")}
        onUpdate={updateLimit}
        disabled={saving}
      />
      {/* Warning */}
      <div className="bg-warning/10 border-warning px-6 py-3 w-fit mt-4">
        <p className="text-sm text-warning">{t("settings.limits.warningCryptoMin")}</p>
      </div>
      <div />
      <h4 className="text-lg font-semibold mt-4 mb-2 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.limits.withdrawalLimits")}</h4>
      <h5 className="text-md font-semibold mt-2 mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.limits.fiatWithdrawals")}</h5>
      <LimitsWithdrawalFields
        idPrefix="withdrawal-fiat"
        basePath={["withdrawal", "fiat"]}
        values={limits.withdrawal.fiat}
        typeLabel={t("settings.limits.types.fiatWithdrawal")}
        onUpdate={updateLimit}
        disabled={saving}
      />
      <h5 className="text-md font-semibold mt-2 mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.limits.cryptoWithdrawals")}</h5>
      <LimitsWithdrawalFields
        idPrefix="withdrawal-crypto"
        basePath={["withdrawal", "crypto"]}
        values={limits.withdrawal.crypto}
        typeLabel={t("settings.limits.types.cryptoWithdrawal")}
        onUpdate={updateLimit}
        disabled={saving}
      />

      <LimitsPerCurrencySection
        currencyLimits={currencyLimits}
        availableCurrencies={availableCurrencies}
        onAddCurrency={addCurrency}
        onRemoveCurrency={removeCurrency}
        onUpdate={updateLimit}
        disabled={saving}
      />

      <SettingsSectionActions
        onSave={handleSave}
        saving={saving}
        resetTarget="limits"
        resetLabel={t("settings.limits.title")}
        onResetDone={handleResetDone}
      />
    </section>
  )
}
